<template>
  <v-app>
    <!-- Barra de navegação principal fixa -->
    <v-app-bar app color="white" dark density="compact">
      <img src="/logo.png" alt="Logo" class="navbar-logo mr-3" />
      <v-toolbar-title>{{ $t('home.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      
      <!-- User Name -->
      <span class="mr-3">{{ username }}</span>
      
      <!-- Refresh Button (only on dashboard) -->
      <v-btn
        v-if="currentRoute === 'dashboard'"
        @click="refreshDashboard"
        :loading="isRefreshing"
        size="small"
        prepend-icon="mdi-refresh"
        variant="text"
      >
        {{ $t('dashboard.refresh') }}
      </v-btn>
      
      <!-- LocalBackend Status Indicator -->
      <v-tooltip bottom>
        <template v-slot:activator="{ props }">
          <v-icon
            v-bind="props"
            :color="localBackendConnected ? 'success' : 'error'"
            class="mr-2"
          >
            {{ localBackendConnected ? 'mdi-lan-connect' : 'mdi-lan-disconnect' }}
          </v-icon>
        </template>
        <span>{{ localBackendConnected ? $t('home.localBackendConnected') : $t('home.localBackendDisconnected') }}</span>
      </v-tooltip>
      
      <!-- Menu -->
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="navigateTo('dashboard')">
            <template v-slot:prepend>
              <v-icon>mdi-view-dashboard</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.dashboard') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('archive')">
            <template v-slot:prepend>
              <v-icon>mdi-archive</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.archive') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('help')">
            <template v-slot:prepend>
              <v-icon>mdi-help-circle</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.help') }}</v-list-item-title>
          </v-list-item>
          
          <v-list-item @click="navigateTo('adm-config')">
            <template v-slot:prepend>
              <v-icon>mdi-cog</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.config') }}</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="userRole === 'superadmin'" @click="navigateTo('users')">
            <template v-slot:prepend>
              <v-icon>mdi-account-group</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.users') }}</v-list-item-title>
          </v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item @click="logout">
            <template v-slot:prepend>
              <v-icon>mdi-logout</v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Conteúdo principal -->
    <v-main>
      <!-- Configuration Error Alert -->
      <v-alert
        v-if="localBackendStore.configurationError"
        type="error"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationError') }}</v-alert-title>
        <div>{{ localBackendStore.configurationError }}</div>
        <div v-if="localBackendStore.missingPaths.length > 0" class="mt-2">
          <strong>{{ $t('home.missingPaths') }}:</strong>
          <ul>
            <li v-for="(path, index) in localBackendStore.missingPaths" :key="index">
              {{ path.name }}: {{ path.reason }}
              <span v-if="path.path" class="text-grey">({{ path.path }})</span>
            </li>
          </ul>
        </div>
        <v-btn
          color="primary"
          variant="text"
          @click="navigateTo('adm-config')"
          class="mt-2"
        >
          {{ $t('home.goToConfiguration') }}
        </v-btn>
      </v-alert>
      
      <!-- Configuration Warnings -->
      <v-alert
        v-if="localBackendStore.warnings.length > 0"
        type="warning"
        variant="tonal"
        closable
        class="ma-4"
      >
        <v-alert-title>{{ $t('home.configurationWarnings') }}</v-alert-title>
        <ul>
          <li v-for="(warning, index) in localBackendStore.warnings" :key="index">
            {{ warning }}
          </li>
        </ul>
      </v-alert>
      
      <router-view />
    </v-main>
  </v-app>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { computed, onMounted, watch, ref } from 'vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const localBackendStore = useLocalBackendStore()
const isRefreshing = ref(false)

const currentRoute = computed(() => route.name)
const localBackendConnected = computed(() => localBackendStore.isConnected)

// Estrai il nome utente dal token JWT
const username = computed(() => {
  const decoded = authStore.decodeToken()
  if (decoded) {
    // Il token JWT potrebbe contenere 'sub', 'username', 'name', etc.
    return decoded.sub || decoded.username || decoded.name || 'User'
  }
  return authStore.user || 'User'
})

const userRole = computed(() => authStore.user?.role)

const navigateTo = (routeName) => {
  router.push({ name: routeName })
}

const logout = async () => {
  authStore.logout()
  router.push('/login')
}

// Funzione per refresh della dashboard
const refreshDashboard = () => {
  // Emette un evento che la dashboard può ascoltare
  window.dispatchEvent(new CustomEvent('dashboard-refresh'))
}

// Check connection on mount and when navigating to dashboard
onMounted(() => {
  localBackendStore.checkConnection()
})

// Check connection when route changes to dashboard
watch(() => route.name, (newRoute) => {
  if (newRoute === 'dashboard') {
    localBackendStore.checkConnection()
  }
})
</script>

<style scoped>
.v-btn--active {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.navbar-logo {
  max-height: 60px;
  width: auto;
}
</style>